import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, hasPerm, fmtDate } from "@/lib/helpers";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Printer, Loader2, Ban, Pencil } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import ReceiptActions from "./ReceiptActions";
import { FeeCardButton } from "@/components/FeeCardDialog";

const Stat = ({ label, value, tone = "slate" }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className={`text-xl font-heading font-bold mt-1 tabular text-${tone}-600`}>{value}</div>
  </div>
);

const PROFILE_FIELDS = [
  ["Date of Birth", "dob", "date"], ["Gender", "gender", "select", ["Male", "Female", "Other"]], ["Blood Group", "blood_group", "select", ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"]],
  ["Height (cm)", "height", "number"], ["Weight (kg)", "weight", "number"], ["Phone", "phone", "text"],
  ["WhatsApp", "whatsapp", "text"], ["Alt Phone", "alt_phone", "text"], ["Address", "address", "text"],
  ["City", "city", "text"], ["State", "state", "text"], ["PIN", "pin_code", "text"],
  ["Aadhar", "aadhar_number", "text"], ["Apaar ID", "apaar_id", "text"], ["Admission In Class", "admission_class", "text"], ["Admission Section", "admission_section", "text"],
  ["Category", "category", "select", ["General", "OBC", "SC", "ST", "Other"]], ["Caste", "caste", "text"], ["Religion", "religion", "text"],
  ["On UDISE", "on_udise", "select", ["Yes", "No"]], ["RTE Student", "rte_student", "select", ["Yes", "No"]],
  ["Fee Promise Date", "promise_date", "date"], ["Fee Promise Amount", "promise_amount", "number"],
  ["Admission Date", "admission_date", "date"], ["TC Status", "tc_status", "select", ["Not Applicable", "Pending", "Received", "Submitted", "Issued"]], ["Session", "academic_session", "text"],
];

export default function StudentProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initTab = sp.get("tab") || "profile";
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [voidOpen, setVoidOpen] = useState(null);
  const [reason, setReason] = useState("");
  const [stmtFrom, setStmtFrom] = useState("");
  const [stmtTo, setStmtTo] = useState("");
  const [tab, setTab] = useState(initTab);
  const [editMode, setEditMode] = useState(false);
  const [pform, setPform] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [classes, setClasses] = useState([]);
  const token = localStorage.getItem("token");

  const load = () => api.get(`/students/${id}`).then((r) => setS(r.data));
  useEffect(() => { load(); }, [id]);
  const [family, setFamily] = useState(null);
  const loadFamily = () => api.get(`/students/${id}/family-fees`).then((r) => setFamily(r.data)).catch(() => setFamily(null));
  useEffect(() => { loadFamily(); }, [id]);
  const canCollect = hasPerm(user, "collect_fee");
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);

  const setP = (k, v) => setPform((p) => ({ ...p, [k]: v }));
  const startEdit = () => { setPform({ ...s }); setTab("profile"); setEditMode(true); };
  const saveProfile = async () => {
    setSavingEdit(true);
    try {
      const payload = { ...pform, promise_amount: Number(pform.promise_amount) || 0, tags: pform.tags || [], tag: (pform.tags && pform.tags[0]) || "" };
      await api.put(`/students/${id}`, payload);
      toast.success("Student updated");
      setEditMode(false);
      await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed to save"); }
    finally { setSavingEdit(false); }
  };
  const classRecords = classes || [];
  const classNames = Array.from(new Set(classRecords.map((c) => c.name).filter(Boolean)));
  const selClass = classRecords.find((c) => c.id === pform.class_id);
  const selClassName = selClass ? selClass.name : "";
  const sectionOpts = Array.from(new Set(classRecords.filter((c) => c.name === selClassName && c.section).map((c) => c.section)));
  const pickClassName = (name) => { const recs = classRecords.filter((c) => c.name === name); const chosen = recs.find((c) => c.section) || recs[0]; setPform((p) => ({ ...p, class_id: chosen ? chosen.id : "", section: chosen ? (chosen.section || "") : "" })); };
  const pickSection = (sec) => { const rec = classRecords.find((c) => c.name === selClassName && c.section === sec); setPform((p) => ({ ...p, section: sec, class_id: rec ? rec.id : p.class_id })); };

  const doVoid = async () => {
    try {
      await api.post(`/fees/void/${voidOpen}`, { reason });
      toast.success("Receipt voided");
      setVoidOpen(null); setReason(""); load();
    } catch (e) { toast.error("Failed to void"); }
  };

  const uploadDoc = (docType) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("File too large (max 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try { await api.patch(`/students/${id}/document`, { doc_type: docType, name: file.name, data: reader.result }); toast.success("Uploaded"); load(); }
      catch (err) { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
  };
  const removeDoc = async (docType) => {
    try { await api.delete(`/students/${id}/document/${docType}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;
  const fs = s.fees_summary;

  return (
    <div className="space-y-6">
      <button onClick={() => nav("/students")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-btn"><ArrowLeft size={16} />Back to Students</button>

      <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-5 flex-wrap">
        <div className="w-20 h-20 rounded-2xl bg-indigo-600 text-white overflow-hidden flex items-center justify-center text-2xl font-heading font-bold">
          {s.photo ? <img src={s.photo} alt={s.name} className="w-full h-full object-cover" /> : s.name?.[0]}
        </div>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">{s.name}</h1>
          <p className="text-slate-500">{s.admission_number} · {s.class_name} {s.section} · Roll {s.roll_number}</p>
        </div>
        <div className="flex items-center gap-3">
          {hasPerm(user, "edit_student") && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={startEdit} data-testid="edit-student-btn"><Pencil size={14} />Edit Student Data</Button>
          )}
          <FeeCardButton studentId={s.id} />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="fee-statement-btn"><Printer size={14} />Fee Statement</Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3 space-y-3" data-testid="fee-statement-popover">
              <div className="text-sm font-semibold text-slate-700">Fee Statement</div>
              <p className="text-xs text-slate-500">Leave dates empty for the full year, or pick a range to cover a single term.</p>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">From</Label><Input type="date" value={stmtFrom} onChange={(e) => setStmtFrom(e.target.value)} data-testid="statement-from" className="h-9" /></div>
                <div><Label className="text-xs">To</Label><Input type="date" value={stmtTo} onChange={(e) => setStmtTo(e.target.value)} data-testid="statement-to" className="h-9" /></div>
              </div>
              <a
                href={`${API}/students/${s.id}/fee-statement-pdf?token=${token}${stmtFrom ? `&from_date=${stmtFrom}` : ""}${stmtTo ? `&to_date=${stmtTo}` : ""}`}
                target="_blank" rel="noreferrer" className="block"
              >
                <Button size="sm" className="w-full gap-1.5" data-testid="statement-download-btn"><Printer size={14} />Download Statement</Button>
              </a>
            </PopoverContent>
          </Popover>
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${fs.due > 0 ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>Due {fmt(fs.due)}</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="fees" data-testid="tab-fees">Fees</TabsTrigger>
          <TabsTrigger value="transport" data-testid="tab-transport">Transport</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="tab-receipts">Receipts</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4 space-y-4">
          {hasPerm(user, "edit_student") && (
            <div className="flex justify-end gap-2">
              {!editMode ? (
                <Button variant="outline" size="sm" className="gap-1.5" onClick={startEdit} data-testid="edit-student-profile-btn"><Pencil size={14} />Edit Student Data</Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={() => setEditMode(false)} data-testid="cancel-edit-student-btn">Cancel</Button>
                  <Button size="sm" onClick={saveProfile} disabled={savingEdit} data-testid="save-edit-student-btn">{savingEdit ? <Loader2 className="animate-spin" size={14} /> : "Save Changes"}</Button>
                </>
              )}
            </div>
          )}
          {editMode ? (
            <>
            <div className="bg-white rounded-xl border border-slate-200 p-6 grid sm:grid-cols-3 gap-4 text-sm" data-testid="profile-edit-basic">
              <div><div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Name</div><Input value={pform.name ?? ""} onChange={(e) => setP("name", e.target.value)} className="h-9" data-testid="edit-field-name" /></div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Admission No</div><Input value={pform.admission_number ?? ""} onChange={(e) => setP("admission_number", e.target.value)} className="h-9" data-testid="edit-field-admission_number" /></div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Roll No</div><Input value={pform.roll_number ?? ""} onChange={(e) => setP("roll_number", e.target.value)} className="h-9" data-testid="edit-field-roll_number" /></div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Class</div>
                <Select value={selClassName} onValueChange={pickClassName}><SelectTrigger className="h-9" data-testid="edit-field-class"><SelectValue placeholder="Class" /></SelectTrigger><SelectContent>{classNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Section</div>
                <Select value={pform.section || ""} onValueChange={pickSection}><SelectTrigger className="h-9" data-testid="edit-field-section"><SelectValue placeholder="Section" /></SelectTrigger><SelectContent>{sectionOpts.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Father's Name</div><Input value={pform.father_name ?? ""} onChange={(e) => setP("father_name", e.target.value)} className="h-9" data-testid="edit-field-father_name" /></div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Admission Type</div>
                <Select value={pform.admission_type || "New"} onValueChange={(v) => setP("admission_type", v)}><SelectTrigger className="h-9" data-testid="edit-field-admission_type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="New">New</SelectItem><SelectItem value="Old">Old</SelectItem></SelectContent></Select>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6 grid sm:grid-cols-3 gap-4 text-sm" data-testid="profile-edit-grid">
              {PROFILE_FIELDS.map(([label, key, type, options]) => (
                <div key={key}>
                  <div className="text-slate-400 text-xs uppercase tracking-wide mb-1">{label}</div>
                  {type === "select" ? (
                    <Select value={pform[key] || "__none__"} onValueChange={(v) => setP(key, v === "__none__" ? "" : v)}>
                      <SelectTrigger className="h-9" data-testid={`edit-field-${key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent><SelectItem value="__none__">— None —</SelectItem>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={type}
                      value={type === "date" ? (pform[key] || "").slice(0, 10) : (pform[key] ?? "")}
                      onChange={(e) => setP(key, e.target.value)}
                      className="h-9"
                      data-testid={`edit-field-${key}`}
                    />
                  )}
                </div>
              ))}
            </div>
            </>
          ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-6 grid sm:grid-cols-3 gap-y-4 gap-x-8 text-sm">
            {[["Date of Birth", s.dob],
              ["Gender", s.gender], ["Blood Group", s.blood_group], ["Height (cm)", s.height], ["Weight (kg)", s.weight], ["Phone", s.phone],
              ["WhatsApp", s.whatsapp], ["Alt Phone", s.alt_phone], ["Address", s.address], ["City", s.city],
              ["State", s.state], ["PIN", s.pin_code], ["Aadhar", s.aadhar_number],
              ["Apaar ID", s.apaar_id], ["Admission In Class", s.admission_class], ["Admission Section", s.admission_section], ["Category", s.category],
              ["Caste", s.caste], ["Religion", s.religion],
              ["On UDISE", s.on_udise], ["RTE Student", s.rte_student],
              ["Tag", (s.tags && s.tags.length) ? s.tags.join(", ") : s.tag], ["Fee Promise Date", s.promise_date],
              ["Fee Promise Amount", s.promise_amount ? fmt(s.promise_amount) : ""],
              ["Admission Date", fmtDate(s.admission_date)], ["TC Status", s.tc_status], ["Session", s.academic_session]].map(([k, v]) => (
              <div key={k}><div className="text-slate-400 text-xs uppercase tracking-wide">{k}</div><div className="text-slate-800 font-medium">{v || "—"}</div></div>
            ))}
          </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-6 mt-4">
            <h4 className="font-semibold text-slate-800 mb-3">Parents / Guardian Details</h4>
            <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="profile-parents-table">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-emerald-50/70 text-slate-700">
                    <th className="text-left px-3 py-2.5 border border-slate-200 w-52 font-bold">Details</th>
                    {[["mother", "Mother"], ["father", "Father"], ["guardian", "Guardian"]].map(([k, l]) => <th key={k} className="text-left px-3 py-2.5 border border-slate-200 font-bold">{l}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[["name", "Name"], ["qualification", "Qualification"], ["res_address", "Residential Address"], ["occupation", "Occupation"], ["off_address", "Official Address"], ["income", "Annual Income"], ["email", "Email Address"], ["mobile", "Mobile No."], ["aadhar", "Aadhar No."], ["photo", "Photo"]].map(([field, label]) => (
                    <tr key={field}>
                      <td className="px-3 py-2 border border-slate-200 font-medium text-slate-700">{label}</td>
                      {["mother", "father", "guardian"].map((role) => {
                        const val = s[`${role}_${field}`];
                        return (
                          <td key={role} className="px-3 py-2 border border-slate-200 text-slate-800">
                            {field === "photo"
                              ? (val ? <a href={val} target="_blank" rel="noreferrer"><img src={val} alt="" className="w-12 h-12 rounded object-cover border border-slate-200" /></a> : <span className="text-slate-300">—</span>)
                              : editMode
                              ? <Input value={pform[`${role}_${field}`] ?? ""} onChange={(e) => setP(`${role}_${field}`, e.target.value)} className="h-8 text-sm" data-testid={`edit-${role}-${field}`} />
                              : (val || <span className="text-slate-300">—</span>)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="fees" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Total" value={fmt(fs.total_payable)} tone="slate" />
            <Stat label="Paid" value={fmt(fs.paid)} tone="emerald" />
            <Stat label="Discount" value={fmt(fs.discount)} tone="amber" />
            <Stat label="Due" value={fmt(fs.due)} tone="rose" />
          </div>
          {family && family.count > 1 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="family-fees-card">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-800">Family / Siblings Fees</h4>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Combined Due</div>
                  <div className="text-xl font-heading font-bold tabular text-rose-600" data-testid="family-combined-due">{fmt(family.combined_due)}</div>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {family.members.map((m) => (
                  <div key={m.id} className="flex items-center justify-between py-2.5 gap-3" data-testid={`family-member-${m.id}`}>
                    <div className="min-w-0">
                      <button type="button" onClick={() => { if (!m.is_self) nav(`/students/${m.id}?tab=fees`); }} className={`font-medium truncate text-left ${m.is_self ? "text-slate-800 cursor-default" : "text-indigo-600 hover:underline"}`}>
                        {m.name}{m.is_self ? <span className="text-xs text-slate-400 font-normal"> (this student)</span> : null}
                      </button>
                      <div className="text-xs text-slate-400 truncate">{m.class_name}{m.admission_number ? ` · ${m.admission_number}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right"><div className="text-xs text-slate-400">Due</div><div className="tabular font-semibold text-rose-600">{fmt(m.due)}</div></div>
                      {canCollect && m.due > 0 && <Button size="sm" variant="outline" onClick={() => nav(`/fees/collect/${m.id}`)} data-testid={`family-collect-${m.id}`}>Collect</Button>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-3">Siblings are grouped by the same phone number. Collect any sibling's fees without leaving this page.</p>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h4 className="font-semibold text-slate-800 mb-3">Fee Breakdown (Annual)</h4>
            {fs.breakdown.map((b, i) => (
              <div key={i} className="flex justify-between text-sm py-1.5 border-b border-slate-50"><span className="text-slate-600">{b.fee_name} <span className="text-xs text-slate-400">({b.frequency} × {fmt(b.unit)})</span></span><span className="tabular font-medium">{fmt(b.amount)}</span></div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="transport" className="mt-4">
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-sm">
            {s.transport ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {[["Route", s.transport.route], ["Pickup Point", s.transport.pickup_point], ["Vehicle", s.transport.vehicle], ["Monthly Fee", fmt(s.transport.transport_fee)]].map(([k, v]) => (
                  <div key={k}><div className="text-slate-400 text-xs uppercase">{k}</div><div className="font-medium text-slate-800">{v}</div></div>
                ))}
              </div>
            ) : <p className="text-slate-400">No transport assigned.</p>}
          </div>
        </TabsContent>

        <TabsContent value="attendance" className="mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Present" value={s.attendance.present} tone="emerald" />
            <Stat label="Absent" value={s.attendance.absent} tone="rose" />
            <Stat label="Leave" value={s.attendance.leave} tone="amber" />
            <Stat label="Percentage" value={`${s.attendance.percentage}%`} tone="indigo" />
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="promotion-history">
            <h4 className="font-semibold text-slate-800 mb-4">Promotion / Migration History</h4>
            {(s.promotions && s.promotions.length) ? (
              <ol className="relative border-l-2 border-indigo-100 ml-2 space-y-5">
                {[...s.promotions].reverse().map((p, i) => (
                  <li key={i} className="ml-5" data-testid={`promotion-item-${i}`}>
                    <span className="absolute -left-[9px] w-4 h-4 rounded-full bg-indigo-500 border-2 border-white" />
                    <div className="text-sm font-medium text-slate-800">
                      {(p.from_class || "—")} {p.from_section} <span className="text-slate-400">({p.from_session || "—"})</span>
                      <span className="mx-2 text-indigo-400">→</span>
                      {(p.to_class || "—")} {p.to_section} <span className="text-slate-400">({p.to_session || "—"})</span>
                    </div>
                    <div className="text-xs text-slate-400">{fmtDate(p.at)} · by {p.by || "—"}{p.due_carried > 0 ? ` · Old Dues carried ${fmt(p.due_carried)}` : ""}</div>
                  </li>
                ))}
              </ol>
            ) : <p className="text-slate-400 text-sm">No promotion history yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <div className="flex flex-wrap gap-2 mb-4" data-testid="doc-checklist">
            {[["Photo", s.photo], ["Aadhar", s.aadhar_document], ["DOB Certificate", s.dob_certificate], ["Transfer Certificate", s.tc_document]].map(([label, val]) => (
              <span key={label} className={`px-3 py-1 rounded-full text-xs font-semibold ${val ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                {val ? "✓" : "✗"} {label}
              </span>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[["aadhar", "Aadhar Card", s.aadhar_document, s.aadhar_document_name],
              ["dob", "DOB Certificate", s.dob_certificate, s.dob_certificate_name],
              ["tc", "Transfer Certificate", s.tc_document, s.tc_document_name]].map(([type, label, docData, docName]) => (
              <div key={type} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`doc-card-${type}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-800">{label}</span>
                  {docData && hasPerm(user, "edit_student") && <button onClick={() => removeDoc(type)} className="text-rose-500 text-xs hover:underline" data-testid={`remove-${type}`}>Remove</button>}
                </div>
                {docData ? (
                  hasPerm(user, "view_student_documents") ? (
                    String(docData).startsWith("data:image") ?
                      <a href={docData} target="_blank" rel="noreferrer"><img src={docData} alt={label} className="w-full max-h-44 object-contain rounded border border-slate-100 bg-slate-50" /></a> :
                      <a href={docData} target="_blank" rel="noreferrer" className="text-indigo-600 text-sm underline break-all" data-testid={`open-${type}`}>Open {docName || "document"}</a>
                  ) : <p className="text-emerald-600 text-sm">Uploaded ✓ (no view permission)</p>
                ) : <p className="text-slate-400 text-sm">Not uploaded</p>}
                {hasPerm(user, "edit_student") && (
                  <div className="mt-3"><Input type="file" accept="image/*,application/pdf" onChange={uploadDoc(type)} data-testid={`upload-${type}`} className="text-xs" /></div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="receipts" className="mt-4">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {s.payments.length === 0 ? <p className="text-slate-400 p-6">No payments yet.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-3">Receipt</th><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Month</th><th className="text-right px-4 py-3">Amount</th><th className="text-left px-4 py-3">Mode</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {s.payments.map((p) => (
                      <tr key={p.id} className={p.status === "Void" ? "opacity-50" : ""}>
                        <td className="px-4 py-3 font-mono text-xs">{p.receipt_number}{p.edited_by && <div className="text-[10px] text-amber-600 mt-0.5" data-testid={`edited-note-${p.receipt_number}`}>edited by {p.edited_by}{p.edited_at ? ` · ${p.edited_at.slice(0, 10)}` : ""}</div>}</td>
                        <td className="px-4 py-3">{fmtDate(p.payment_date)}</td>
                        <td className="px-4 py-3">{p.fee_month}</td>
                        <td className="px-4 py-3 text-right font-semibold tabular">{fmt(p.amount_paid)}</td>
                        <td className="px-4 py-3">{p.payment_mode}</td>
                        <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "Void" ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-600"}`}>{p.status}</span></td>
                        <td className="px-4 py-3 text-right flex gap-2 justify-end">
                          <a href={`${API}/receipts/${p.id}/pdf?token=${token}`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" data-testid={`print-${p.receipt_number}`}><Printer size={14} /></Button></a>
                          {hasPerm(user, "void_payment") && p.status !== "Void" && (
                            <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => setVoidOpen(p.id)} data-testid={`void-${p.receipt_number}`}><Ban size={14} /></Button>
                          )}
                          <ReceiptActions payment={p} onChanged={load} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!voidOpen} onOpenChange={(o) => !o && setVoidOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Void / Cancel Receipt</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">This keeps an audit trail. Provide a reason.</p>
          <div><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="void-reason-input" /></div>
          <DialogFooter><Button variant="destructive" onClick={doVoid} disabled={!reason} data-testid="confirm-void-btn">Void Receipt</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
